/**
 * CustomerViewsMenu — Customers' part of the Sales flyout (C3; ROLLOUT-CUSTOMERS-SHELL.md §2.5,
 * §3 D1/D8/D9). The registry's flyout group renders it in two places, as QuoteViewsMenu is:
 * nested under Customers in the Sales flyout (rail), and inline under the Customers row
 * (expanded sidebar, "Show customer views").
 *
 *   Views   All, Recent, Open quotes, At risk, and Unassigned for the strict manager roles
 *           (admin, senior_manager, manager: the server's set, utils/customerViews.js). The
 *           review queue stays its own page (D9); the list's header links to it on Unassigned.
 *   Counts  GET /api/customers/stats/view-counts, for the q and scope of the customers list on
 *           screen (the URL's, which the list writes after its 300 ms debounce, so the counts
 *           never follow single keystrokes). Cached 60 s on this device, per q and scope. A
 *           clear (a delete) refetches once in every menu still mounted (polish 15).
 *
 * Every entry is a link (a menuitem): choosing one writes ?view= and nothing else — no
 * crm.ui.sidebar.v1, no preferences write. The user's default view (customers.list.defaultView,
 * C4b) is the bare /customers, so its link carries no ?view=. Colours are tokens only.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cachedFetch } from '../../services/apiCache';
import { useAuth } from '../../contexts/AuthContext';
import { CUSTOMER_MANAGER_ROLES, isCustomerManager } from '../../utils/customerRoles';
import { CUSTOMERS_ROOT, customerViewHref, parseCustomerLocation, useCustomerListDefaults } from './useCustomerRoute';
import '../layout/Flyout.css';
import './CustomerViewsMenu.css';

export const VIEW_COUNTS_PATH = '/api/customers/stats/view-counts';
export const VIEW_COUNTS_TTL_MS = 60 * 1000;

/** The roles the server offers `unassigned` to (strict: no inheritance): the one role model. */
export const UNASSIGNED_ROLES = CUSTOMER_MANAGER_ROLES;
export const canSeeUnassigned = (user) => isCustomerManager(user);

/** §3 D1, in menu order. `tone` is the --status-<tone> family of its dot. */
export const CUSTOMER_VIEW_ENTRIES = [
  { id: 'all', label: 'All', tone: 'expired' },
  { id: 'recent', label: 'Recent', tone: 'info' },
  { id: 'open_quotes', label: 'Open quotes', tone: 'sent' },
  { id: 'at_risk', label: 'At risk', tone: 'error' },
  { id: 'unassigned', label: 'Unassigned', tone: 'warning', managersOnly: true }
];

export const customerViewLabel = (view) =>
  (CUSTOMER_VIEW_ENTRIES.find((v) => v.id === view) || CUSTOMER_VIEW_ENTRIES[0]).label;

/** The counts URL for a list's q and scope (q from 2 characters, as the list sends it). */
export function customerViewCountsUrl({ q = '', scope = 'my' } = {}) {
  const sp = new URLSearchParams();
  const term = String(q || '').trim();
  if (term.length >= 2) sp.set('q', term);
  if (scope && scope !== 'my') sp.set('scope', scope);
  const qs = sp.toString();
  return `${VIEW_COUNTS_PATH}${qs ? `?${qs}` : ''}`;
}

// url -> { at, promise }: one request per q and scope per 60 s, shared by every menu mounted
// (the flyout and the inline list can both be on screen).
const countsCache = new Map();

// Bumped by every clear. A mounted useCustomerViewCounts subscribes to it, so a clear while it is
// on screen (the views menu left open across a delete, the palette's view hints) refetches once:
// the first hook to run misses the emptied cache, the others share its request. Nothing cleared,
// nothing sent.
let countsVersion = 0;
const versionListeners = new Set();
const subscribeVersion = (listener) => {
  versionListeners.add(listener);
  return () => versionListeners.delete(listener);
};
const getVersion = () => countsVersion;

/** Forget the cached counts; every mounted counts hook fetches them again (once). */
export function clearCustomerViewCounts() {
  countsCache.clear();
  countsVersion += 1;
  versionListeners.forEach((listener) => listener());
}

function loadCounts(url) {
  const hit = countsCache.get(url);
  if (hit && Date.now() - hit.at < VIEW_COUNTS_TTL_MS) return hit.promise;
  // apiCache's own TTL for /api/customers is 5 minutes; this cache is the 60 s one.
  const promise = cachedFetch(url, { skipCache: true }).then((body) => body?.views || null);
  countsCache.set(url, { at: Date.now(), promise });
  promise.catch(() => {
    if (countsCache.get(url)?.promise === promise) countsCache.delete(url);
  });
  return promise;
}

/** The views block for the customers list on screen (or the default list elsewhere). */
export function useCustomerViewCounts() {
  const location = useLocation();
  const onCustomers = location.pathname === CUSTOMERS_ROOT || location.pathname.startsWith(`${CUSTOMERS_ROOT}/`);
  const list = onCustomers ? parseCustomerLocation(location.pathname, location.search).list : null;
  const url = customerViewCountsUrl({ q: list?.q, scope: list?.scope });
  const version = useSyncExternalStore(subscribeVersion, getVersion, getVersion);
  const [state, setState] = useState({ url: null, views: null });
  useEffect(() => {
    let alive = true;
    loadCounts(url)
      .then((views) => { if (alive) setState({ url, views }); })
      .catch(() => { /* no counts: the views still work as links */ });
    return () => { alive = false; };
  }, [url, version]);
  // Counts for another q are not this list's: show none until this q's arrive. After a clear the
  // same q's last counts stay until the fresh ones land (no blank flash).
  return state.url === url ? state.views : null;
}

/**
 * @param {object} props
 * @param {boolean} [props.inline] the expanded sidebar's child list (no heading)
 */
export default function CustomerViewsMenu({ inline = false }) {
  const { user } = useAuth();
  const location = useLocation();
  const counts = useCustomerViewCounts();
  const defaults = useCustomerListDefaults(user);
  const onList = location.pathname === CUSTOMERS_ROOT;
  const activeView = onList ? parseCustomerLocation(location.pathname, location.search, defaults).list.view : null;
  const entries = CUSTOMER_VIEW_ENTRIES.filter((v) => !v.managersOnly || canSeeUnassigned(user));

  return (
    <div className={`customer-views-menu${inline ? ' customer-views-menu-inline' : ''}`}>
      <div role="group" aria-label="Customer views">
        {!inline && <div className="shell-flyout-heading" aria-hidden="true">Customer views</div>}
        <ul className="shell-flyout-list" role="none">
          {entries.map((v) => {
            const count = counts && Number.isFinite(counts[v.id]) ? counts[v.id] : null;
            const current = activeView === v.id;
            return (
              <li key={v.id} role="none">
                <Link
                  to={customerViewHref(v.id, location, defaults)}
                  role="menuitem"
                  aria-label={count === null ? v.label : `${v.label} (${count})`}
                  aria-current={current ? 'page' : undefined}
                  className={`shell-flyout-item customer-views-item${current ? ' active' : ''}`}
                  style={{ '--customer-view-tone': `var(--status-${v.tone})` }}
                >
                  <span className="customer-views-dot" aria-hidden="true" />
                  <span className="customer-views-label">{v.label}</span>
                  <span className="customer-views-count" aria-hidden="true">{count === null ? '' : count}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
